import React, { useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { useInsightsEvents } from 'dashboard/hooks/useInsightsEvents';
import { InsightsEvent } from 'dashboard/types';
import EventCard from 'dashboard/components/EventCard';
import { usePosthogCapture } from 'dashboard/posthogCapture/_index';

import * as OverviewStyled from 'dashboard/styles/Overview.style';
import * as Styled from 'dashboard/styles/Events.style';
import * as Buttons from 'theme/components/Buttons.style';
import * as Text from 'theme/components/Text.style';

interface EventsSectionProps {
  queryParams: string;
  onEventSelect: (eventId: string) => void;
}

const PAGE_SIZE = 10;

function EventsContent({
  events,
  totalCount,
  isLoading,
  onLoadMore,
  onEventSelect,
  t,
}: {
  events: InsightsEvent[] | undefined;
  totalCount: number;
  isLoading: boolean;
  onLoadMore: () => void;
  onEventSelect: (eventId: string) => void;
  t: (key: string, options?: Record<string, unknown>) => string;
}) {
  if (isLoading) {
    return (
      <OverviewStyled.Card>
        <Text.H3>–</Text.H3>
      </OverviewStyled.Card>
    );
  }

  if (!events?.length) {
    return (
      <OverviewStyled.Card>
        <Text.Label>{t('events.noEvents')}</Text.Label>
      </OverviewStyled.Card>
    );
  }

  const hasMore = events.length < totalCount;

  return (
    <>
      <Styled.CardList>
        {events.map((event) => (
          <EventCard key={event.id} event={event} onSelect={onEventSelect} />
        ))}
      </Styled.CardList>

      {hasMore && (
        <Styled.LoadMoreContainer>
          <Text.P1>
            {t('events.viewedCount', {
              viewed: events.length,
              total: totalCount,
            })}
          </Text.P1>
          <Buttons.SecondaryLight $size="small" onClick={onLoadMore}>
            {t('events.loadMore')}
          </Buttons.SecondaryLight>
        </Styled.LoadMoreContainer>
      )}
    </>
  );
}

export default function EventsSection({
  queryParams,
  onEventSelect,
}: EventsSectionProps): JSX.Element {
  const { t } = useTranslation('pageDashboard');
  const dashboard = usePosthogCapture();
  const { data, isLoading, hasNextPage, fetchNextPage } =
    useInsightsEvents(queryParams);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  const allEvents = useMemo(
    () => data?.pages.flatMap((page) => page.results) ?? [],
    [data]
  );
  const totalCount = data?.pages[0]?.count ?? 0;
  const events = allEvents.slice(0, visibleCount);

  const handleLoadMore = useCallback(() => {
    const nextVisible = visibleCount + PAGE_SIZE;
    if (nextVisible > allEvents.length && hasNextPage) {
      fetchNextPage();
    }
    setVisibleCount(nextVisible);
    dashboard.eventsLoadedMore();
  }, [visibleCount, allEvents.length, hasNextPage, fetchNextPage, dashboard]);

  return (
    <OverviewStyled.SectionContainer id="events-section">
      <Styled.SectionTitleGroup>
        <Text.H5>{t('events.title')}</Text.H5>
        <Styled.SectionDescription>
          {t('events.description')}
        </Styled.SectionDescription>
      </Styled.SectionTitleGroup>

      <EventsContent
        events={events}
        totalCount={totalCount}
        isLoading={isLoading}
        onLoadMore={handleLoadMore}
        onEventSelect={onEventSelect}
        t={t}
      />
    </OverviewStyled.SectionContainer>
  );
}
